import type { AppConfig } from '../services/config';

/**
 * Runtime configuration override (issue #1).
 *
 * The web app is a static SPA with no build-time env injection, so the Cognito pool/client ids
 * and API host are normally baked in per-environment (`vantage-app-config.*.ts`). That goes stale
 * if the Cognito pool is ever recreated (AWS issues a new id) and forces `PLACEHOLDER_*` literals
 * in the committed production config. Instead, the container entrypoint renders a `/config.json`
 * (via `envsubst` from the same SSM values the backend uses) which the app fetches at boot and
 * merges over the bundled fallback — so a pool recreate self-heals on the next task restart.
 *
 * This module holds the pure merge logic; the fetch + `provideAppInitializer` wiring lives in the
 * app's `app.config.ts`.
 */

/** String keys accepted from `/config.json` (camelCase, mirroring {@link AppConfig}). */
const RUNTIME_KEYS = [
  'apiBaseUrl',
  'subApiBaseUrl',
  'clientApiBaseUrl',
  'publicApiBaseUrl',
  'cognitoUserPoolId',
  'cognitoClientId',
  'cognitoRegion',
  // Stripe **publishable** key (`pk_…`) — safe to expose to the browser and it differs per
  // environment (test vs live, per Stripe account), so it belongs in runtime config (SSM-rendered
  // in deploys; a gitignored local config.json in dev) rather than baked into a committed bundle.
  'stripePublishableKey',
] as const satisfies readonly (keyof AppConfig)[];

/**
 * A runtime value is usable only if it is a non-empty string with no un-substituted `${…}`
 * placeholder — so a missing env var (entrypoint left `${COGNITO_USER_POOL_ID}` verbatim) or an
 * absent file degrades to the bundled fallback rather than breaking login with a literal `${…}`.
 */
function usable(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && !value.includes('${');
}

/**
 * Merge runtime overrides from a fetched `/config.json` into the bundled fallback config IN PLACE
 * (the `APP_CONFIG` holder keeps a stable object reference, so `ConfigService` sees the result).
 * Only well-formed string values override; anything missing/empty/`${…}` keeps the fallback.
 * Returns the keys that were actually overridden (handy for diagnostics/tests).
 */
export function applyRuntimeConfig(target: AppConfig, raw: unknown): readonly string[] {
  if (!raw || typeof raw !== 'object') return [];
  const src = raw as Record<string, unknown>;
  const applied: string[] = [];
  for (const key of RUNTIME_KEYS) {
    if (usable(src[key])) {
      (target as unknown as Record<string, unknown>)[key] = src[key];
      applied.push(key);
    }
  }
  // `paymentsPreview` is a dev-only BOOLEAN switch (deferred-intent preview, no backend, no charge —
  // `docs/payments/plan.md` A7), so it can't ride the string allow-list above. Accept it only as a
  // real boolean. Deploy-time config.json (rendered by infra from a fixed SSM key set) never carries
  // it, so it stays off in real environments; a local gitignored config.json can flip it on.
  if (typeof src['paymentsPreview'] === 'boolean') {
    target.paymentsPreview = src['paymentsPreview'];
    applied.push('paymentsPreview');
  }
  return applied;
}
